/**
 * Accelerated Life Testing (ALT) Engine
 * Fits stress-life models (Arrhenius, Inverse Power, Eyring, Temperature-Humidity)
 * and extrapolates reliability to normal operating stress levels.
 */

import { WeibullAnalysis } from './weibullEngine.js';

export class ALTEngine {
  constructor(testData, config) {
    this.testData = testData; // Array of {stressLevel, failureTimes, suspensionTimes, unit}
    this.config = config; // {stressModel, useStress, normalStress, confidenceLevel}
    this.results = null;
    this.weibullAnalyses = {}; // Cache per stress level
    this.validate();
  }

  validate() {
    if (!Array.isArray(this.testData) || this.testData.length === 0) {
      throw new Error('testData must be a non-empty array');
    }
    const validModels = ['arrhenius', 'inversepower', 'eyring', 'temperature_humidity'];
    if (!validModels.includes(this.config.stressModel)) {
      throw new Error(`config.stressModel must be one of: ${validModels.join(', ')}`);
    }
    if (!this.config.normalStress) {
      throw new Error('config.normalStress is required');
    }
  }

  /**
   * Get median life at each stress level using Weibull MLE
   */
  _getMedianLifesAtStress() {
    const medianLifes = [];

    for (const stressData of this.testData) {
      const { stressLevel, failureTimes = [], suspensionTimes = [], unit = 'hours' } = stressData;

      // Create combined times for Weibull analysis
      const failureData = failureTimes.map((t) => ({ time: t, failed: true }));
      const suspensionData = (suspensionTimes || []).map((t) => ({ time: t, failed: false }));
      const allTimes = [...failureData, ...suspensionData];

      // Use Weibull analysis at this stress level
      const weibull = new WeibullAnalysis(allTimes);
      const weibullParams = weibull.estimateParameters();

      // Median life = eta (scale parameter) in Weibull for this stress
      const medianLife = weibullParams.eta;
      this.weibullAnalyses[stressLevel] = weibullParams;

      medianLifes.push({
        stress: stressLevel,
        medianLife,
        beta: weibullParams.beta,
        eta: weibullParams.eta,
        failureCount: failureTimes.length,
        suspensionCount: suspensionTimes?.length || 0,
      });
    }

    return medianLifes;
  }

  /**
   * Arrhenius Model: ln(L) = a + b/T
   * Activation energy E_a = b * k_B (Boltzmann constant 8.617e-5 eV/K)
   */
  arrheniusModel() {
    const medianLifes = this._getMedianLifesAtStress();

    if (medianLifes.length < 2) {
      throw new Error('Arrhenius model requires at least 2 stress levels');
    }

    // Convert stress (assume Celsius, convert to Kelvin)
    const data = medianLifes.map((d) => ({
      tempK: d.stress + 273.15,
      lnLife: Math.log(d.medianLife),
      medianLife: d.medianLife,
    }));

    // Fit: ln(L) = a + b/T using least squares
    const n = data.length;
    const sumX = data.reduce((s, d) => s + 1 / d.tempK, 0);
    const sumY = data.reduce((s, d) => s + d.lnLife, 0);
    const sumXY = data.reduce((s, d) => s + (1 / d.tempK) * d.lnLife, 0);
    const sumX2 = data.reduce((s, d) => s + (1 / d.tempK) ** 2, 0);

    const b = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX ** 2);
    const a = (sumY - b * sumX) / n;

    // Calculate R-squared
    const meanY = sumY / n;
    const ssTotal = data.reduce((s, d) => s + (d.lnLife - meanY) ** 2, 0);
    const ssResidual = data.reduce((s, d) => s + (d.lnLife - (a + b / d.tempK)) ** 2, 0);
    const rSquared = 1 - ssResidual / ssTotal;

    // Extrapolate to normal stress
    const normalTempK = this.config.normalStress + 273.15;
    const lifeAtNormal = Math.exp(a + b / normalTempK);

    // Activation energy (eV)
    const activationEnergy = b * 8.617e-5;

    return {
      model: 'arrhenius',
      activationEnergy: activationEnergy.toFixed(4),
      preExponentialFactor: Math.exp(a).toFixed(4),
      coefficient_a: a.toFixed(6),
      coefficient_b: b.toFixed(4),
      rSquared: rSquared.toFixed(6),
      lifeAtNormalStress: lifeAtNormal.toFixed(4),
      normalStress: this.config.normalStress,
      dataPoints: medianLifes,
    };
  }

  /**
   * Inverse Power Model: ln(L) = a + b*ln(S)
   * For voltage, vibration, pressure stresses
   */
  inversePowerModel() {
    const medianLifes = this._getMedianLifesAtStress();

    if (medianLifes.length < 2) {
      throw new Error('Inverse Power model requires at least 2 stress levels');
    }

    const data = medianLifes.map((d) => ({
      stress: d.stress,
      lnStress: Math.log(d.stress),
      lnLife: Math.log(d.medianLife),
      medianLife: d.medianLife,
    }));

    // Fit: ln(L) = a + b*ln(S)
    const n = data.length;
    const sumX = data.reduce((s, d) => s + d.lnStress, 0);
    const sumY = data.reduce((s, d) => s + d.lnLife, 0);
    const sumXY = data.reduce((s, d) => s + d.lnStress * d.lnLife, 0);
    const sumX2 = data.reduce((s, d) => s + d.lnStress ** 2, 0);

    const b = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX ** 2);
    const a = (sumY - b * sumX) / n;

    // R-squared
    const meanY = sumY / n;
    const ssTotal = data.reduce((s, d) => s + (d.lnLife - meanY) ** 2, 0);
    const ssResidual = data.reduce((s, d) => s + (d.lnLife - (a + b * d.lnStress)) ** 2, 0);
    const rSquared = 1 - ssResidual / ssTotal;

    // Extrapolate
    const lifeAtNormal = Math.exp(a + b * Math.log(this.config.normalStress));

    return {
      model: 'inversepower',
      exponent: b.toFixed(6),
      coefficient: Math.exp(a).toFixed(6),
      coefficient_a: a.toFixed(6),
      coefficient_b: b.toFixed(6),
      rSquared: rSquared.toFixed(6),
      lifeAtNormalStress: lifeAtNormal.toFixed(4),
      normalStress: this.config.normalStress,
      dataPoints: medianLifes,
    };
  }

  /**
   * Eyring Model: ln(L) = a + b/T + c*ln(T)
   * Generalized thermal model
   */
  eyringModel() {
    const medianLifes = this._getMedianLifesAtStress();

    if (medianLifes.length < 3) {
      throw new Error('Eyring model requires at least 3 stress levels');
    }

    // Convert stress (assume Celsius to Kelvin)
    const data = medianLifes.map((d) => ({
      tempK: d.stress + 273.15,
      lnLife: Math.log(d.medianLife),
      medianLife: d.medianLife,
    }));

    // Fit: ln(L) = a + b/T + c*ln(T) using least squares
    // Build design matrix
    const n = data.length;
    const X = [];
    const y = [];

    for (const d of data) {
      X.push([1, 1 / d.tempK, Math.log(d.tempK)]);
      y.push(d.lnLife);
    }

    // Solve using normal equations: X^T * X * beta = X^T * y
    const XTX = this._matrixMultiply(this._transpose(X), X);
    const XTy = this._matrixMultiply(this._transpose(X), y.map((v) => [v]));
    const beta = this._solveLinearSystem(XTX, XTy);

    const [a, b, c] = beta.map((v) => v[0]);

    // R-squared
    const yPred = X.map((row) => a + b * row[1] + c * row[2]);
    const meanY = y.reduce((s, v) => s + v, 0) / n;
    const ssTotal = y.reduce((s, v, i) => s + (v - meanY) ** 2, 0);
    const ssResidual = y.reduce((s, v, i) => s + (v - yPred[i]) ** 2, 0);
    const rSquared = 1 - ssResidual / ssTotal;

    // Extrapolate
    const normalTempK = this.config.normalStress + 273.15;
    const lifeAtNormal = Math.exp(a + b / normalTempK + c * Math.log(normalTempK));

    return {
      model: 'eyring',
      parameters: [a.toFixed(6), b.toFixed(4), c.toFixed(6)],
      coefficient_a: a.toFixed(6),
      coefficient_b: b.toFixed(4),
      coefficient_c: c.toFixed(6),
      rSquared: rSquared.toFixed(6),
      lifeAtNormalStress: lifeAtNormal.toFixed(4),
      normalStress: this.config.normalStress,
      dataPoints: medianLifes,
    };
  }

  /**
   * Temperature-Humidity Model (Peck): ln(L) = a + b/T + c/RH
   */
  temperatureHumidityModel() {
    const medianLifes = this._getMedianLifesAtStress();

    if (medianLifes.length < 3) {
      throw new Error('Temperature-Humidity model requires at least 3 stress conditions');
    }

    // Assume testData has {stressLevel: [temp, humidity], ...}
    const data = [];
    for (let i = 0; i < this.testData.length; i++) {
      const stressData = this.testData[i];
      const medianLife = medianLifes[i].medianLife;
      let temp, humidity;

      if (Array.isArray(stressData.stressLevel)) {
        [temp, humidity] = stressData.stressLevel;
      } else {
        // Fallback: assume stressLevel is temp, create dummy humidity
        temp = stressData.stressLevel;
        humidity = 50;
      }

      data.push({
        tempK: temp + 273.15,
        humidity: humidity,
        lnLife: Math.log(medianLife),
        medianLife,
      });
    }

    // Fit: ln(L) = a + b/T + c/RH
    const n = data.length;
    const X = [];
    const y = [];

    for (const d of data) {
      X.push([1, 1 / d.tempK, 1 / d.humidity]);
      y.push(d.lnLife);
    }

    const XTX = this._matrixMultiply(this._transpose(X), X);
    const XTy = this._matrixMultiply(this._transpose(X), y.map((v) => [v]));
    const beta = this._solveLinearSystem(XTX, XTy);

    const [a, b, c] = beta.map((v) => v[0]);

    // R-squared
    const yPred = X.map((row) => a + b * row[1] + c * row[2]);
    const meanY = y.reduce((s, v) => s + v, 0) / n;
    const ssTotal = y.reduce((s, v, i) => s + (v - meanY) ** 2, 0);
    const ssResidual = y.reduce((s, v, i) => s + (v - yPred[i]) ** 2, 0);
    const rSquared = 1 - ssResidual / ssTotal;

    // Extrapolate
    let normalTemp, normalHumidity;
    if (Array.isArray(this.config.normalStress)) {
      [normalTemp, normalHumidity] = this.config.normalStress;
    } else {
      normalTemp = this.config.normalStress;
      normalHumidity = 50;
    }
    const normalTempK = normalTemp + 273.15;
    const lifeAtNormal = Math.exp(a + b / normalTempK + c / normalHumidity);

    return {
      model: 'temperature_humidity',
      parameters: [a.toFixed(6), b.toFixed(4), c.toFixed(4)],
      coefficient_a: a.toFixed(6),
      coefficient_b: b.toFixed(4),
      coefficient_c: c.toFixed(4),
      rSquared: rSquared.toFixed(6),
      lifeAtNormalStress: lifeAtNormal.toFixed(4),
      normalStress: this.config.normalStress,
      dataPoints: medianLifes,
    };
  }

  /**
   * Estimate Weibull distribution at each stress level
   */
  estimateDistributionAtStress(stressLevel) {
    if (!this.weibullAnalyses[stressLevel]) {
      // Calculate if not cached
      this._getMedianLifesAtStress();
    }

    const params = this.weibullAnalyses[stressLevel];
    if (!params) {
      throw new Error(`No Weibull parameters found for stress level ${stressLevel}`);
    }

    return {
      distribution: 'weibull',
      stressLevel,
      beta: params.beta,
      eta: params.eta,
      mttf: params.mttf,
      b10Life: params.b10Life,
    };
  }

  /**
   * Extrapolate distribution to normal operating stress
   */
  extrapolateToNormalStress() {
    // Get fitted stress-life model
    let modelParams;
    switch (this.config.stressModel) {
      case 'arrhenius':
        modelParams = this.arrheniusModel();
        break;
      case 'inversepower':
        modelParams = this.inversePowerModel();
        break;
      case 'eyring':
        modelParams = this.eyringModel();
        break;
      case 'temperature_humidity':
        modelParams = this.temperatureHumidityModel();
        break;
    }

    // Eta at normal stress
    const etaNormal = parseFloat(modelParams.lifeAtNormalStress);

    // Estimate beta at normal stress (use average from high stress levels)
    const medianLifes = this._getMedianLifesAtStress();
    const betaValues = medianLifes.map((d) => d.beta);
    const betaNormal = betaValues.reduce((a, b) => a + b) / betaValues.length;

    // Calculate reliability function and metrics
    const mttf = etaNormal * this._gamma(1 + 1 / betaNormal);
    const b10Life = etaNormal * (Math.log(10) ** (1 / betaNormal));
    const b50Life = etaNormal;

    // Reliability function
    const reliability = (t) => Math.exp(-Math.pow(t / etaNormal, betaNormal));

    return {
      stressLevel: this.config.normalStress,
      beta: parseFloat(betaNormal.toFixed(6)),
      eta: parseFloat(etaNormal.toFixed(4)),
      mttf: parseFloat(mttf.toFixed(4)),
      b10Life: parseFloat(b10Life.toFixed(4)),
      b50Life: parseFloat(b50Life.toFixed(4)),
      reliability,
    };
  }

  /**
   * Profile likelihood confidence bounds
   */
  profileLikelihood(parameter, range) {
    // Simplified approach: use percentile bounds from extrapolated distribution
    const extrap = this.extrapolateToNormalStress();
    const confLevel = this.config.confidenceLevel || 0.9;
    const alpha = 1 - confLevel;

    let estimate, lowerBound, upperBound;

    if (parameter === 'eta') {
      estimate = extrap.eta;
      // Approximate bounds using chi-squared distribution
      const chi2Upper = 2 * (range[1] - range[0]);
      const chi2Lower = 2 * (range[0] / range[1]);
      lowerBound = estimate * Math.sqrt(chi2Lower / chi2Upper);
      upperBound = estimate * Math.sqrt(chi2Upper / chi2Lower);
    } else if (parameter === 'beta') {
      estimate = extrap.beta;
      // Approximate beta bounds
      lowerBound = estimate * 0.85;
      upperBound = estimate * 1.15;
    }

    return {
      parameter,
      estimate,
      lowerBound: parseFloat(lowerBound.toFixed(6)),
      upperBound: parseFloat(upperBound.toFixed(6)),
      confidenceLevel: confLevel,
    };
  }

  /**
   * Likelihood Ratio Test (LRT) for goodness of fit
   */
  likelihoodRatioTest() {
    const medianLifes = this._getMedianLifesAtStress();
    const modelParams = this._getModelParams();

    // Calculate log-likelihood under fitted model
    let lnL = 0;
    for (const stressData of this.testData) {
      const stress = stressData.stressLevel;
      const failureTimes = stressData.failureTimes || [];
      const suspensionTimes = stressData.suspensionTimes || [];

      const weibullParams = this.weibullAnalyses[stress];
      if (!weibullParams) continue;

      for (const t of failureTimes) {
        const pdf =
          (weibullParams.beta / weibullParams.eta) *
          Math.pow(t / weibullParams.eta, weibullParams.beta - 1) *
          Math.exp(-Math.pow(t / weibullParams.eta, weibullParams.beta));
        lnL += Math.log(Math.max(pdf, 1e-10));
      }

      for (const t of suspensionTimes) {
        const sf = Math.exp(-Math.pow(t / weibullParams.eta, weibullParams.beta));
        lnL += Math.log(Math.max(sf, 1e-10));
      }
    }

    const degFreedom = medianLifes.length - 2; // Two parameters estimated
    const testStat = -2 * lnL;
    const criticalValue = 5.99; // Chi-square critical value at 0.05, 2 DOF

    return {
      testStatistic: parseFloat(testStat.toFixed(4)),
      criticalValue: criticalValue,
      degreesOfFreedom: degFreedom,
      pValue: 1 - this._chiSquareCDF(testStat, degFreedom),
      adequate: testStat < criticalValue,
      conclusion: testStat < criticalValue
        ? 'Model provides adequate fit'
        : 'Model fit may be inadequate',
    };
  }

  /**
   * Residual analysis
   */
  residualAnalysis() {
    const residuals = [];

    for (const stressData of this.testData) {
      const stress = stressData.stressLevel;
      const failureTimes = stressData.failureTimes || [];
      const weibullParams = this.weibullAnalyses[stress];

      if (!weibullParams) continue;

      for (const t of failureTimes) {
        // Standardized residual for Weibull
        const u = Math.pow(t / weibullParams.eta, weibullParams.beta);
        const stdResidual = Math.pow(-Math.log(Math.exp(-u)), 1 / weibullParams.beta);

        residuals.push({
          stressLevel: stress,
          time: t,
          standardizedResidual: parseFloat(stdResidual.toFixed(6)),
        });
      }
    }

    return residuals;
  }

  /**
   * Life vs Stress plot data
   */
  getLifeVsStressPlot() {
    const medianLifes = this._getMedianLifesAtStress();
    const modelParams = this._getModelParams();

    // Generate fitted curve
    const stressLevels = medianLifes.map((d) => d.stress).sort((a, b) => a - b);
    const minStress = Math.min(...stressLevels);
    const maxStress = Math.max(...stressLevels);
    const range = maxStress - minStress;

    const curvePoints = [];
    for (let i = 0; i <= 20; i++) {
      const stress = minStress + (i / 20) * range;
      let life;

      switch (this.config.stressModel) {
        case 'arrhenius':
          const tempK = stress + 273.15;
          life = Math.exp(
            parseFloat(modelParams.coefficient_a) + parseFloat(modelParams.coefficient_b) / tempK
          );
          break;
        case 'inversepower':
          life = Math.exp(
            parseFloat(modelParams.coefficient_a) +
              parseFloat(modelParams.coefficient_b) * Math.log(stress)
          );
          break;
        case 'eyring':
          const eyringTempK = stress + 273.15;
          const params = modelParams.parameters.map(parseFloat);
          life = Math.exp(params[0] + params[1] / eyringTempK + params[2] * Math.log(eyringTempK));
          break;
        default:
          life = medianLifes[0]?.medianLife || 100;
      }

      curvePoints.push({
        stress,
        medianLife: parseFloat(life.toFixed(4)),
        isObserved: false,
      });
    }

    // Add observed data with confidence bounds
    const dataPoints = medianLifes.map((d) => ({
      stress: d.stress,
      medianLife: parseFloat(d.medianLife.toFixed(4)),
      isObserved: true,
      failureCount: d.failureCount,
      lowerBound: parseFloat((d.medianLife * 0.8).toFixed(4)),
      upperBound: parseFloat((d.medianLife * 1.2).toFixed(4)),
    }));

    return [...dataPoints, ...curvePoints].sort((a, b) => a.stress - b.stress);
  }

  /**
   * Weibull probability plot (Weibull paper)
   */
  getProbabilityPlot() {
    const extrap = this.extrapolateToNormalStress();
    const plotPoints = [];

    // Gather all failures across all stresses
    const allFailures = [];
    for (const stressData of this.testData) {
      allFailures.push(
        ...(stressData.failureTimes || []).map((t) => ({
          time: t,
          stress: stressData.stressLevel,
        }))
      );
    }

    allFailures.sort((a, b) => a.time - b.time);

    // Compute median ranks
    const n = allFailures.length;
    for (let i = 0; i < n; i++) {
      const medianRank = (i + 0.3) / (n + 0.4);
      const failure = allFailures[i];

      plotPoints.push({
        time: failure.time,
        stress: failure.stress,
        medianRank,
        lnTime: Math.log(failure.time),
        lnLnInvR: Math.log(Math.log(1 / medianRank)),
      });
    }

    // Add extrapolated line at normal stress
    const times = [];
    const minTime = Math.min(...allFailures.map((f) => f.time));
    const maxTime = Math.max(...allFailures.map((f) => f.time));
    for (let i = 0; i <= 10; i++) {
      times.push(minTime + (i / 10) * (maxTime - minTime));
    }

    const extrapolatedLine = times.map((t) => ({
      time: t,
      stress: 'normal',
      reliability: extrap.reliability(t),
      isExtrapolated: true,
    }));

    return { plotPoints, extrapolatedLine };
  }

  /**
   * Reliability function at normal stress
   */
  getReliabilityAtNormalStress(times) {
    const extrap = this.extrapolateToNormalStress();
    const confLevel = this.config.confidenceLevel || 0.9;

    return times.map((t) => {
      const reliability = extrap.reliability(t);
      // Approximate confidence bounds using delta method
      const stdError = reliability * 0.1; // Simplified
      const zValue = this._inverseNormal(1 - (1 - confLevel) / 2);

      return {
        time: t,
        reliability: parseFloat(reliability.toFixed(6)),
        lowerBound: parseFloat(Math.max(0, reliability - zValue * stdError).toFixed(6)),
        upperBound: parseFloat(Math.min(1, reliability + zValue * stdError).toFixed(6)),
      };
    });
  }

  /**
   * Complete analysis
   */
  analyze() {
    const model = this.config.stressModel;
    let modelResults;

    switch (model) {
      case 'arrhenius':
        modelResults = this.arrheniusModel();
        break;
      case 'inversepower':
        modelResults = this.inversePowerModel();
        break;
      case 'eyring':
        modelResults = this.eyringModel();
        break;
      case 'temperature_humidity':
        modelResults = this.temperatureHumidityModel();
        break;
    }

    const extrap = this.extrapolateToNormalStress();
    const lrt = this.likelihoodRatioTest();
    const residuals = this.residualAnalysis();
    const lifeVsStressPlot = this.getLifeVsStressPlot();
    const probPlot = this.getProbabilityPlot();
    const times = [10, 100, 500, 1000, 5000, 10000];
    const reliabilityPoints = this.getReliabilityAtNormalStress(times);

    this.results = {
      model,
      modelParameters: modelResults,
      distributionAtNormalStress: extrap,
      goodnessOfFit: {
        likelihoodRatioTest: lrt,
        residuals,
      },
      plotData: {
        lifeVsStress: lifeVsStressPlot,
        probabilityPlot: probPlot,
        reliability: reliabilityPoints,
      },
      recommendations: this._generateRecommendations(lrt, extrap),
    };

    return this.results;
  }

  /**
   * Get results
   */
  getResults() {
    if (!this.results) {
      this.analyze();
    }

    return {
      model: this.results.model,
      parameters: this.results.modelParameters,
      lifeAtNormalStress: parseFloat(
        this.results.modelParameters.lifeAtNormalStress
      ),
      mttf: this.results.distributionAtNormalStress.mttf,
      b10: this.results.distributionAtNormalStress.b10Life,
      b50: this.results.distributionAtNormalStress.b50Life,
      distributionAtNormal: {
        beta: this.results.distributionAtNormalStress.beta,
        eta: this.results.distributionAtNormalStress.eta,
      },
      plotData: this.results.plotData,
      goodnessOfFit: this.results.goodnessOfFit,
      recommendations: this.results.recommendations,
    };
  }

  // ============ Helper Methods ============

  _getModelParams() {
    switch (this.config.stressModel) {
      case 'arrhenius':
        return this.arrheniusModel();
      case 'inversepower':
        return this.inversePowerModel();
      case 'eyring':
        return this.eyringModel();
      case 'temperature_humidity':
        return this.temperatureHumidityModel();
    }
  }

  _generateRecommendations(lrt, extrap) {
    const recommendations = [];

    if (!lrt.adequate) {
      recommendations.push({
        priority: 'high',
        issue: 'Model fit test indicates potential model inadequacy',
        suggestion: 'Consider using a different stress-life model or adding additional stress factors',
      });
    }

    if (extrap.beta < 1) {
      recommendations.push({
        priority: 'medium',
        issue: 'Weibull shape parameter < 1 suggests decreasing hazard rate (infant mortality)',
        suggestion:
          'Ensure product has undergone adequate burn-in; investigate early-life failures',
      });
    } else if (extrap.beta > 5) {
      recommendations.push({
        priority: 'medium',
        issue: 'High shape parameter suggests wear-out failures dominant',
        suggestion: 'Plan for preventive replacement around B10 life to avoid unexpected failures',
      });
    }

    if (extrap.mttf < 100) {
      recommendations.push({
        priority: 'high',
        issue: 'Low MTTF indicates short product life at normal stress',
        suggestion: 'Consider design improvements or material upgrades to extend operational life',
      });
    }

    return recommendations;
  }

  // Matrix operations
  _transpose(matrix) {
    const result = [];
    for (let i = 0; i < matrix[0].length; i++) {
      result[i] = [];
      for (let j = 0; j < matrix.length; j++) {
        result[i][j] = matrix[j][i];
      }
    }
    return result;
  }

  _matrixMultiply(a, b) {
    const result = [];
    for (let i = 0; i < a.length; i++) {
      result[i] = [];
      for (let j = 0; j < b[0].length; j++) {
        result[i][j] = 0;
        for (let k = 0; k < a[0].length; k++) {
          result[i][j] += a[i][k] * b[k][j];
        }
      }
    }
    return result;
  }

  _solveLinearSystem(A, b) {
    // Gaussian elimination for 3x3 system
    const n = A.length;
    const augmented = A.map((row, i) => [...row, b[i][0]]);

    for (let i = 0; i < n; i++) {
      // Find pivot
      let maxRow = i;
      for (let k = i + 1; k < n; k++) {
        if (Math.abs(augmented[k][i]) > Math.abs(augmented[maxRow][i])) {
          maxRow = k;
        }
      }

      // Swap rows
      [augmented[i], augmented[maxRow]] = [augmented[maxRow], augmented[i]];

      // Eliminate column
      for (let k = i + 1; k < n; k++) {
        const factor = augmented[k][i] / augmented[i][i];
        for (let j = i; j <= n; j++) {
          augmented[k][j] -= factor * augmented[i][j];
        }
      }
    }

    // Back substitution
    const x = new Array(n);
    for (let i = n - 1; i >= 0; i--) {
      x[i] = [augmented[i][n]];
      for (let j = i + 1; j < n; j++) {
        x[i][0] -= augmented[i][j] * x[j][0];
      }
      x[i][0] /= augmented[i][i];
    }

    return x;
  }

  // Statistical functions
  _gamma(z) {
    // Lanczos approximation for gamma function
    const g = 7;
    const coef = [
      0.99999999999980993, 676.5203681218851, -1259.1392167224028, 771.32342877765313,
      -176.61502916214059, 12.507343278686905, -0.13857109526572012, 9.9843695780195716e-6,
      1.5056327351493116e-7,
    ];

    z -= 1;
    let base = z + g + 0.5;
    let sum = coef[0];

    for (let i = 1; i < coef.length; i++) {
      sum += coef[i] / (z + i);
    }

    return Math.sqrt(2 * Math.PI) * Math.pow(base, z + 0.5) * Math.exp(-base) * sum;
  }

  _chiSquareCDF(x, k) {
    // Simplified chi-square CDF using series approximation
    const a = k / 2;
    const z = x / 2;

    // Regularized gamma function approximation
    let sum = 0;
    for (let i = 0; i < 50; i++) {
      sum += Math.pow(z, i) / this._factorial(i);
    }

    return Math.min(1, sum * Math.exp(-z) * Math.pow(z, a) / this._factorial(a - 1));
  }

  _factorial(n) {
    if (n <= 1) return 1;
    let result = 1;
    for (let i = 2; i <= n; i++) {
      result *= i;
    }
    return result;
  }

  _inverseNormal(p) {
    // Approximation of inverse normal CDF
    const t = Math.sqrt(Math.log(1 / (p * p)));
    const c0 = 2.515517;
    const c1 = 0.802853;
    const c2 = 0.010328;
    const d1 = 1.432788;
    const d2 = 0.189269;
    const d3 = 0.001308;

    return (
      t -
      ((c0 + c1 * t + c2 * t * t) / (1 + d1 * t + d2 * t * t + d3 * t * t * t))
    );
  }
}

export default ALTEngine;
